import { api } from "~/utils/api"
import type { ReturnedVariant } from "~/server/api/routers/variantTableData"
import { runPartialRefresh, type PartialRefreshOps } from "~/utils/cacheUtils/runPartialRefresh"

/**
 * Merges new variant data into existing React Query cache without losing unrelated data.
 * Uses variantId as the key to efficiently update only changed variants while preserving others.
 */
function mergeVariantsIntoCache(
  existingData: ReturnedVariant[] | undefined,
  updatedData: ReturnedVariant[] | undefined,
): ReturnedVariant[] {
  if (!existingData) {
    return Array.isArray(updatedData) ? updatedData : []
  }

  if (!updatedData || updatedData.length === 0) {
    return existingData
  }

  // Untouched variants keep their objects: the variant table rebuilds only rows whose inputs changed.
  const variantMap = new Map(existingData.map((variant) => [variant.variantId, variant]))
  for (const updatedVariant of updatedData) {
    variantMap.set(updatedVariant.variantId, updatedVariant)
  }

  return Array.from(variantMap.values())
}

/**
 * Updates the variant cache with fresh data for specific variants.
 * Implements the cache refresh pattern with proper error handling and race condition prevention.
 */
export function variantsRefreshOps(
  utils: ReturnType<typeof api.useUtils>,
  variantIds: string[],
): PartialRefreshOps<ReturnedVariant[]> {
  return {
    cancel: () => {
      // Only a refetch can land on top of the merge; a cancelled first load is never restarted.
      if (utils.variantTableData.variants.getData({})) {
        void utils.variantTableData.variants.cancel()
      }
    },
    // Outside the query cache, so a concurrent refresh's cancel() cannot abort this slice.
    fetch: () => utils.client.variantTableData.variants.query({ variantIds }),
    apply: (updatedVariants) => {
      if (updatedVariants.length === 0) return
      utils.variantTableData.variants.setData({}, (oldData) => {
        if (!oldData) return oldData
        const updated = mergeVariantsIntoCache(oldData, updatedVariants)
        if (!updated || updated.length === 0) {
          console.warn("Cache merge returned empty data, keeping old data")
          return oldData
        }
        return updated
      })
    },
    fallback: () => {
      void utils.variantTableData.variants.invalidate()
    },
  }
}

async function refreshVariantsCache(utils: ReturnType<typeof api.useUtils>, variantIds: string[]) {
  if (!variantIds || variantIds.length === 0) return
  await runPartialRefresh(variantsRefreshOps(utils, variantIds))
}

/**
 * Hook for partial variant cache refresh.
 * Encapsulates global state dependencies (utils) for cleaner usage in mutations.
 *
 * @returns Function that accepts variantIds and refreshes their data in cache
 *
 * @example
 * const refreshVariants = usePartialRefreshVariants()
 * // In mutation onSuccess:
 * await refreshVariants(variables.variantIds)
 */
export function usePartialRefreshVariants() {
  const utils = api.useUtils()

  return async (variantIds: string[]) => {
    await refreshVariantsCache(utils, variantIds)
  }
}
